import { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';

import HttpClient from '../../shared/infrastructures/clients/http.client';
import { extractTranslatedApiErrorMessage } from '../../shared/utils/translate-api-error.util';
import { ADMIN_DELIVERY_CITIES_API_PATHS } from '../../delivery-cities/constants/delivery-cities-api-paths.constants';
import { DEFAULT_DELIVERY_HORIZON_DAYS } from '../constants/delivery-schedule.constants';
import { DeliveryDaysCity, GetDeliveryCityDeliveryDaysResponse } from '../types/api-delivery-day.type';
import { DeliveryDay } from '../types/delivery-day.type';
import { toDeliveryDays } from '../utils/format-delivery-schedule.util';
import { isNotFoundError } from '../utils/is-not-found-error.util';

type UseDeliveryDaysResult = {
  isLoading: boolean;
  deliveryCity: DeliveryDaysCity | null;
  days: DeliveryDay[];
  hasNoDeliverableCity: boolean;
};

type DeliveryDaysState = {
  zipCode: string;
  result: UseDeliveryDaysResult;
};

const INITIAL_STATE: UseDeliveryDaysResult = {
  isLoading: false,
  deliveryCity: null,
  days: [],
  hasNoDeliverableCity: false,
};

export function useDeliveryDays({ zipCode }: { zipCode: string }): UseDeliveryDaysResult {
  const { enqueueSnackbar } = useSnackbar();
  const [state, setState] = useState<DeliveryDaysState>({ zipCode: '', result: INITIAL_STATE });

  useEffect(() => {
    if (zipCode === '') return undefined;

    let isCurrent = true;
    setState({ zipCode, result: { ...INITIAL_STATE, isLoading: true } });

    HttpClient.get<GetDeliveryCityDeliveryDaysResponse>(ADMIN_DELIVERY_CITIES_API_PATHS.GET_DELIVERY_DAYS, {
      params: { zipCode, days: DEFAULT_DELIVERY_HORIZON_DAYS },
    })
      .then((response) => {
        if (!isCurrent) return;
        setState({
          zipCode,
          result: {
            isLoading: false,
            deliveryCity: response.deliveryCity,
            days: toDeliveryDays(response.days),
            hasNoDeliverableCity: false,
          },
        });
      })
      .catch((error) => {
        if (!isCurrent) return;
        if (isNotFoundError(error)) {
          setState({ zipCode, result: { ...INITIAL_STATE, hasNoDeliverableCity: true } });
          return;
        }
        enqueueSnackbar(
          extractTranslatedApiErrorMessage(
            error,
            'Une erreur est survenue lors de la récupération des jours de livraison'
          ),
          { variant: 'error' }
        );
        setState({ zipCode, result: INITIAL_STATE });
      });

    return () => {
      isCurrent = false;
    };
  }, [zipCode, enqueueSnackbar]);

  if (state.zipCode !== zipCode) return { ...INITIAL_STATE, isLoading: zipCode !== '' };

  return state.result;
}
